import React, { FormEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { Helmet } from 'react-helmet';

import Add from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  COMMISSION_RULE_CONDITION_TYPES,
  CommissionsConditionTypes,
  SORTED_COMMISSION_RULE_CONDITION_TYPES,
  ensureAgentHubBrands,
  ruleConditionsToTextMap,
} from '~/consts/agentHub';

import { useGetAgenciesById } from '~/hooks/apis/agent/useGetAgenciesById';
import { useGetUsersSummaryByIds } from '~/hooks/apis/agent/useGetUsersSummaryByIds';
import { useListCommissions } from '~/hooks/apis/agent/useListCommissions';
import { useGetPlaceNamesByIds } from '~/hooks/apis/search/useGetPlaceNamesByIds';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import { Commission, getCommissionsParams } from '~/types/services/agentHub';

import { arrayToObject } from '~/utils/arrayUtils';
import { getFlightLocationAdditionalInfo } from '~/utils/commissionFlightLocations';
import { capitalise } from '~/utils/stringUtils';

import { AgentHubCommissionRegions } from './AgentHubCommissionRegions';
import { AgentHubCommissionRules } from './AgentHubCommissionRules';
import CommissionDialog from './AgentHubCommissions/CommissionDialog';
import DeleteCommissionDialog from './AgentHubCommissions/DeleteCommissionDialog';

const DEFAULT_SIZE_PER_PAGE = 10;
type CommissionSearchRegion = NonNullable<getCommissionsParams['region']>;
type CommissionTypeFilter = NonNullable<getCommissionsParams['type']>;
const DEFAULT_REGION: CommissionSearchRegion = 'AU';
const REGION_OPTIONS: Array<CommissionSearchRegion> = ['AU', 'NZ', 'US', 'GB'];
const REGION_OPTION_VALUES = new Set<string>(REGION_OPTIONS);
const COMMISSION_TYPE_OPTIONS: Array<CommissionTypeFilter> = ['base', 'additional', 'incentive'];
const COMMISSION_TYPE_OPTION_VALUES = new Set<string>(COMMISSION_TYPE_OPTIONS);
const CONDITION_TYPE_OPTION_VALUES = new Set<string>(COMMISSION_RULE_CONDITION_TYPES);

function isCommissionSearchRegion(region: string | null): region is CommissionSearchRegion {
  return Boolean(region && REGION_OPTION_VALUES.has(region));
}

function isCommissionTypeFilter(type: string | null): type is CommissionTypeFilter {
  return Boolean(type && COMMISSION_TYPE_OPTION_VALUES.has(type));
}

function parseConditionFilters(conditions: string | null): Array<CommissionsConditionTypes> {
  if (!conditions) {
    return [];
  }

  return conditions
    .split(',')
    .map((condition) => condition.trim())
    .filter((condition): condition is CommissionsConditionTypes => CONDITION_TYPE_OPTION_VALUES.has(condition));
}

function getCommissionLocationPlaceIds(commission: Commission): Array<string> {
  const groupPlaceIds = commission.commissionGroups.flatMap((group) =>
    group.conditions.filter((condition) => condition.type === 'location').map((condition) => condition.value),
  );
  const rulePlaceIds = (commission.rules?.location ?? []).filter((value): value is string => typeof value === 'string');

  return [...groupPlaceIds, ...rulePlaceIds];
}

function AgentCommissions() {
  const queryFilters = useQuery();
  const [tabValue, setTabValue] = useState(0);
  const tenant = useCurrentTenant();

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const filter = useMemo(() => {
    const region = queryFilters.get('region');
    const type = queryFilters.get('type');

    return {
      page: parseInt(queryFilters.get('page') ?? '0'),
      offerId: queryFilters.get('offerId'),
      region: isCommissionSearchRegion(region) ? region : DEFAULT_REGION,
      type: isCommissionTypeFilter(type) ? type : undefined,
      conditions: parseConditionFilters(queryFilters.get('conditions')),
    };
  }, [queryFilters]);

  const [typeInput, setTypeInput] = useState<CommissionTypeFilter | ''>(filter.type ?? '');
  const [conditionsInput, setConditionsInput] = useState<Array<CommissionsConditionTypes>>(filter.conditions);

  useEffect(() => {
    setTypeInput(filter.type ?? '');
    setConditionsInput(filter.conditions);
  }, [filter.conditions, filter.type]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [commissionData, setCommissionData] = useState<Commission | undefined>();
  const [mode, setMode] = useState<'create' | 'edit'>(null);
  const { push: setQueryString } = useAppNavigation();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const { data: { rows: commissionList = [], total = 0 } = {}, isFetching: isListLoading } = useListCommissions({
    page: filter.page.toString(),
    pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
    ruleStatus: tabValue === 0 ? 'active' : 'inactive',
    ...(filter.offerId ? { offerId: filter.offerId } : {}),
    region: filter.region,
    ...(filter.type ? { type: filter.type } : {}),
    ...(filter.conditions.length > 0 ? { conditions: filter.conditions.join(',') } : {}),
  });

  // Extract all agent and agency IDs from commission list
  const allAgentIds = useMemo(() => {
    return commissionList.flatMap((row) =>
      row.commissionGroups.flatMap((group) =>
        group.conditions.filter((condition) => condition.type === 'agent').map((condition) => condition.value),
      ),
    );
  }, [commissionList]);

  const allAgencyIds = useMemo(() => {
    return commissionList.flatMap((row) =>
      row.commissionGroups.flatMap((group) =>
        group.conditions.filter((condition) => condition.type === 'agency').map((condition) => condition.value),
      ),
    );
  }, [commissionList]);

  const allLocationPlaceIds = useMemo(() => {
    return commissionList.flatMap(getCommissionLocationPlaceIds);
  }, [commissionList]);

  // Fetch agents and agencies using React Query
  const { data: agentsMap } = useGetUsersSummaryByIds(allAgentIds, ensureAgentHubBrands(tenant.tenant.brand));
  const { data: agenciesData } = useGetAgenciesById(allAgencyIds);
  const { data: locations = {} } = useGetPlaceNamesByIds(allLocationPlaceIds);

  // Transform agents and agencies data to label maps
  const agents = useMemo(() => {
    if (!agentsMap) {
      return {};
    }
    const agentsArray = Array.from(agentsMap.values());
    return arrayToObject(
      agentsArray,
      (agent) => agent.id_member,
      (agent) => `${agent.full_name} [${agent.email}]`,
    );
  }, [agentsMap]);

  const agencies = useMemo(() => {
    if (!agenciesData) {
      return {};
    }
    return arrayToObject(
      agenciesData,
      (agency) => agency.id,
      (agency) => agency.name,
    );
  }, [agenciesData]);

  const onPageChange = useCallback(
    (page: number) => {
      queryFilters.set('page', page.toString());
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onOfferIdChange = useCallback(
    (offerId: string) => {
      if (offerId === '') {
        queryFilters.delete('offerId');
      } else {
        queryFilters.set('offerId', offerId);
      }
      queryFilters.set('page', '0');
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onOfferIdChangeDebounced = useMemo(
    () => debounce((offerId: string) => onOfferIdChange(offerId), 500),
    [onOfferIdChange],
  );

  const onRegionChange = useCallback(
    (event: SelectChangeEvent) => {
      queryFilters.set('region', event.target.value);
      queryFilters.set('page', '0');
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const handleTypeInputChange = useCallback((event: SelectChangeEvent) => {
    setTypeInput(event.target.value as CommissionTypeFilter | '');
  }, []);

  const handleConditionsInputChange = useCallback((event: SelectChangeEvent<Array<string> | string>) => {
    const value = event.target.value;
    setConditionsInput(
      (typeof value === 'string' ? value.split(',') : value).filter(
        (condition): condition is CommissionsConditionTypes => CONDITION_TYPE_OPTION_VALUES.has(condition),
      ),
    );
  }, []);

  const handleSearchSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (typeInput) {
        queryFilters.set('type', typeInput);
      } else {
        queryFilters.delete('type');
      }

      if (conditionsInput.length > 0) {
        queryFilters.set('conditions', conditionsInput.join(','));
      } else {
        queryFilters.delete('conditions');
      }

      queryFilters.set('page', '0');
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [conditionsInput, queryFilters, setQueryString, typeInput],
  );

  const handleClearFilters = useCallback(() => {
    setTypeInput('');
    setConditionsInput([]);
    queryFilters.delete('type');
    queryFilters.delete('conditions');
    queryFilters.set('page', '0');
    setQueryString({
      search: queryFilters.toString(),
    });
  }, [queryFilters, setQueryString]);

  const sharedOpts: Partial<GridColDef> = {
    editable: false,
    sortable: true,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    flex: 2,
  };

  const columns: Array<GridColDef> = [
    {
      ...sharedOpts,
      field: 'description',
      headerName: 'Description',
      renderHeader: () => <Typography variant="button">Description</Typography>,
      flex: 1.5,
    },
    {
      ...sharedOpts,
      field: 'commissionPercentage',
      headerName: 'Commission',
      renderHeader: () => <Typography variant="button">Commission</Typography>,
      flex: 1,
      renderCell: (params) => `${params.value}%`,
    },
    {
      ...sharedOpts,
      field: 'commissionType',
      headerName: 'Commission Type',
      flex: 1,
      renderHeader: () => <Typography variant="button">Commission Type</Typography>,
      renderCell: (params) => {
        const row = params.row as Commission;
        if (row.type === 'base' && row.priority != null) {
          return `Base (P=${row.priority})`;
        }
        return capitalise(params.value as string);
      },
    },
    {
      ...sharedOpts,
      field: 'limit',
      headerName: 'Limit',
      flex: 1,
      renderHeader: () => <Typography variant="button">Limit</Typography>,
      renderCell: (params) => (params.value ? params.value.toString() : '-'),
    },
    {
      ...sharedOpts,
      field: 'regions',
      headerName: 'Regions',
      flex: 1,
      renderCell: (params) => <AgentHubCommissionRegions regions={params.value} />,
      renderHeader: () => <Typography variant="button">Regions</Typography>,
    },
    {
      ...sharedOpts,
      field: 'commissionGroups',
      headerName: 'Conditions',
      renderCell: (params) => {
        const row = params.row as Commission;
        return (
          <AgentHubCommissionRules
            commissionGroups={row.commissionGroups}
            rules={row.rules}
            additionalInfo={getFlightLocationAdditionalInfo(row)}
            agents={agents}
            agencies={agencies}
            locations={locations}
          />
        );
      },
      renderHeader: () => <Typography variant="button">Conditions</Typography>,
    },
    {
      ...sharedOpts,
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderHeader: () => <Typography variant="button">Actions</Typography>,
      renderCell: (params) => (
        <Stack spacing={1} alignItems="center" direction="row">
          <Button
            sx={{ padding: 0, margin: 0, display: 'inline-block', minHeight: 0, minWidth: 0 }}
            onClick={() => {
              openModal('edit');
              setCommissionData(params.row);
            }}
          >
            <ModeEditIcon sx={{ color: 'text.primary' }} />
          </Button>
          <Button
            sx={{ padding: 0, margin: 0, display: 'inline-block', minHeight: 0, minWidth: 0 }}
            onClick={() => {
              openDeleteModal();
              setCommissionData(params.row);
            }}
          >
            <DeleteIcon sx={{ color: 'text.primary' }} />
          </Button>
        </Stack>
      ),
    },
  ];

  const openModal = (mode: 'create' | 'edit') => {
    setMode(mode);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setCommissionData(null);
    setIsModalOpen(false);
  };

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Agent Commissions</title>
      </Helmet>

      <Grid item xs={12}>
        <Stack pt={2} direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <PageSubheader title="Commission rules" />
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            <Tooltip title="Offer search uses offer metadata only. Commissions that depend on dates, flight details, cart totals, or specific agent context may not be shown.">
              <IconButton aria-label="Offer search information" size="small">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="commission-offer-region-label">Region</InputLabel>
              <Select
                labelId="commission-offer-region-label"
                id="commission-offer-region"
                data-testid="commission-offer-region"
                name="region"
                label="Region"
                value={filter.region}
                onChange={onRegionChange}
              >
                {REGION_OPTIONS.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              id="offer-id-search"
              data-testid="offer-id-search"
              type="text"
              name="offerId"
              label="Offer ID"
              defaultValue={filter.offerId ?? ''}
              onChange={(event) => onOfferIdChangeDebounced(event.target.value)}
              sx={{ width: 320 }}
            />

            <Divider orientation="vertical" flexItem />

            {tabValue === 0 && (
              <Button
                sx={{ textTransform: 'none' }}
                startIcon={<Add />}
                variant="contained"
                onClick={() => openModal('create')}
              >
                Create new rule
              </Button>
            )}
          </Stack>
        </Stack>

        <Box component="form" onSubmit={handleSearchSubmit} sx={{ pt: 2, pb: 2 }}>
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="commission-type-filter-label" shrink>
                Type
              </InputLabel>
              <Select
                labelId="commission-type-filter-label"
                id="commission-type-filter"
                data-testid="commission-type-filter"
                name="type"
                label="Type"
                value={typeInput}
                onChange={handleTypeInputChange}
                displayEmpty
                input={<OutlinedInput label="Type" notched />}
                renderValue={(value) => {
                  if (!value) {
                    return <Typography color="text.secondary">All types</Typography>;
                  }
                  return capitalise(value);
                }}
              >
                <MenuItem value="">All types</MenuItem>
                {COMMISSION_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {capitalise(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 280 }}>
              <InputLabel id="commission-conditions-filter-label">Conditions</InputLabel>
              <Select<Array<string>>
                labelId="commission-conditions-filter-label"
                id="commission-conditions-filter"
                data-testid="commission-conditions-filter"
                name="conditions"
                label="Conditions"
                multiple
                value={conditionsInput}
                onChange={handleConditionsInputChange}
                input={<OutlinedInput label="Conditions" />}
                renderValue={(selected) =>
                  selected
                    .map(
                      (condition) => ruleConditionsToTextMap.get(condition as CommissionsConditionTypes) ?? condition,
                    )
                    .join(', ')
                }
              >
                {SORTED_COMMISSION_RULE_CONDITION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Checkbox checked={conditionsInput.includes(type.value)} />
                    <ListItemText primary={type.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button type="submit" variant="contained" color="primary">
              Search
            </Button>
            <Button type="button" variant="text" color="primary" onClick={handleClearFilters}>
              Clear filters
            </Button>
          </Stack>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="commission tabs">
            <Tab label="Active" />
            <Tab label="Inactive" />
          </Tabs>
        </Box>

        <Box sx={{ pt: 3 }}>
          <DataGrid
            loading={isListLoading}
            columns={columns}
            getRowId={(row) => row.id}
            rows={commissionList}
            autoHeight
            rowCount={total}
            paginationMode="server"
            paginationModel={{ page: filter.page, pageSize: DEFAULT_SIZE_PER_PAGE }}
            pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
            onPaginationModelChange={({ page }) => onPageChange(page)}
            getRowHeight={() => 'auto'}
          />
        </Box>
      </Grid>

      <CommissionDialog mode={mode} isOpen={isModalOpen} onClose={closeModal} data={commissionData} />
      <DeleteCommissionDialog data={commissionData} isOpen={isDeleteModalOpen} onClose={closeDeleteModal} />
    </Container>
  );
}

export default AgentCommissions;
